import { useLiveQuery } from "dexie-react-hooks";
import { db, type Project, type Task, type TimeBlock } from "@/lib/db";
import { addDays, localDateKey } from "@/lib/time";

// Live views over the local store: they update the moment a write lands in
// Dexie (from this tab or a sync pull), with no network round trip.

const live = (t: Task) => !t.deleted_at;

/** Unsorted captures, oldest first so the backlog gets cleared in order. */
export function useInboxTasks(): Task[] | undefined {
	return useLiveQuery(
		() =>
			db.tasks
				.where("status")
				.equals("inbox")
				.filter((t) => live(t) && !t.parent_id)
				.sortBy("created_at"),
		[],
	);
}

/** Tasks for `day`: due that day or pinned as its must-wins; dropped ones hidden. */
export function useDayTasks(day: string): Task[] | undefined {
	return useLiveQuery(async () => {
		const [due, pinned] = await Promise.all([
			db.tasks.where("due_date").equals(day).toArray(),
			db.tasks.where("must_win_date").equals(day).toArray(),
		]);
		const byId = new Map([...due, ...pinned].map((t) => [t.id, t]));
		return [...byId.values()]
			.filter((t) => live(t) && t.status !== "dropped")
			.sort(
				(a, b) =>
					(a.due_at ?? "\uffff").localeCompare(b.due_at ?? "\uffff") ||
					a.created_at.localeCompare(b.created_at),
			);
	}, [day]);
}

/** The block happening now, or the next one later today. */
export function useNextBlock(now: Date, day: string): TimeBlock | null | undefined {
	return useLiveQuery(async () => {
		const blocks = await db.time_blocks.filter((b) => !b.deleted_at).toArray();
		const t = now.getTime();
		return (
			blocks
				.filter((b) => Date.parse(b.end_at) > t && localDateKey(new Date(b.start_at)) === day)
				.sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at))[0] ?? null
		);
	}, [now.getTime(), day]);
}

/**
 * Unfinished tasks from before `today` (PL-6). They keep their old date until
 * the owner triages them; nothing is moved silently.
 */
export function useCarriedOver(today: string): Task[] | undefined {
	return useLiveQuery(
		() =>
			db.tasks
				.where("due_date")
				.below(today)
				.filter((t) => live(t) && t.status === "todo")
				.sortBy("due_date"),
		[today],
	);
}

/** Blocks that touch `day`, earliest first. */
export function useDayBlocks(day: string): TimeBlock[] | undefined {
	return useLiveQuery(async () => {
		const blocks = await db.time_blocks.filter((b) => !b.deleted_at).toArray();
		return blocks
			.filter((b) => localDateKey(new Date(b.start_at)) === day)
			.sort((a, b) => Date.parse(a.start_at) - Date.parse(b.start_at));
	}, [day]);
}

/** One task by id; null once it is deleted or missing. */
export function useTask(id: string | null): Task | null | undefined {
	return useLiveQuery(async () => {
		if (!id) return null;
		const t = await db.tasks.get(id);
		return t && live(t) ? t : null;
	}, [id]);
}

/** A task's subtasks in the order they were added. */
export function useSubtasks(parentId: string): Task[] | undefined {
	return useLiveQuery(
		() => db.tasks.where("parent_id").equals(parentId).filter(live).sortBy("created_at"),
		[parentId],
	);
}

/** All projects (archived last), alphabetical within each group. */
export function useProjects(): Project[] | undefined {
	return useLiveQuery(async () => {
		const all = await db.projects.filter((p) => !p.deleted_at).toArray();
		return all.sort(
			(a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name),
		);
	}, []);
}

export function useProject(id: string | null): Project | null | undefined {
	return useLiveQuery(async () => {
		if (!id) return null;
		const p = await db.projects.get(id);
		return p && !p.deleted_at ? p : null;
	}, [id]);
}

export type WeekDay = { day: string; tasks: Task[]; plannedMin: number };

/** Seven days from `start`: their top-level tasks and minutes of blocks planned. */
export function useWeek(start: string): WeekDay[] | undefined {
	return useLiveQuery(async () => {
		const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
		const [tasks, blocks] = await Promise.all([
			db.tasks.where("due_date").between(days[0], days[6], true, true).toArray(),
			db.time_blocks.filter((b) => !b.deleted_at).toArray(),
		]);
		return days.map((day) => ({
			day,
			tasks: tasks
				.filter((t) => t.due_date === day && live(t) && t.status !== "dropped" && !t.parent_id)
				.sort((a, b) => Number(a.status === "done") - Number(b.status === "done")),
			plannedMin: blocks
				.filter((b) => localDateKey(new Date(b.start_at)) === day)
				.reduce((sum, b) => sum + (Date.parse(b.end_at) - Date.parse(b.start_at)) / 60_000, 0),
		}));
	}, [start]);
}
